import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import CustomRichTextEditor from '@/components/CustomRichTextEditor'
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/Multiselect'
import { Info } from 'lucide-react'
import { FileUpload } from '@/components/ui/file-upload'
import {
  transformCategoriesToOptions,
  transformTagToOptions,
  type CategoryData,
  type TagData,
} from '@/utils/helper'
import { cn } from '@/lib/utils'
import { useFormContext, Controller } from 'react-hook-form'
import type { EventFormDataType } from '@/schemas/eventForm.schema'

export interface URLImageFile extends File {
  isExisting?: boolean
  imageId?: string
  imageKey?: string
}

interface EventBasicInfoFormProps {
  handleImageSelect: (
    files: File | File[] | null,
    type: 'eventImage' | 'guestImage',
    id?: string
  ) => Promise<void>
  handleRemoveImage: (index: number, type: 'eventImage' | 'guestImage') => void
  handleSelectPDF: (file: File | null) => void
  handleRemovePDF: () => void
  handleSelectInfoPDF: (file: File | null) => void
  handleRemoveInfoPDF: () => void
  categoryData?: CategoryData[]
  tagData?: TagData[]
}

export default function EventBasicInfoForm(
  props: Readonly<EventBasicInfoFormProps>
) {
  const {
    handleImageSelect,
    handleRemoveImage,
    handleSelectPDF,
    handleRemovePDF,
    handleSelectInfoPDF,
    handleRemoveInfoPDF,
    categoryData = [],
    tagData = [],
  } = props

  const { control } = useFormContext<EventFormDataType>()

  const tagOptions = transformTagToOptions(tagData) || []
  const multiSelectOptions = tagOptions.map(t => ({
    id: t.id,
    name: t.name,
    title: t.title,
  }))

  const handleTagChange = (
    newIds: string[],
    onChange: (value: EventFormDataType['tag']) => void
  ) => {
    if (newIds.length > 10) return
    const newTagObjects = newIds.reduce<TagData[]>((acc, id) => {
      const tag = tagData.find(t => t.id === id)
      if (tag) acc.push(tag)
      return acc
    }, [])
    onChange(newTagObjects)
  }

  return (
    <>
      <CardHeader className="pb-4 border-b border-black/6 bg-ss-neutral-25 flex items-center gap-2 px-4 py-3 [.border-b]:pb-3 rounded-tl-xl rounded-tr-xl ">
        <Info className="h-5 w-5 text-ss-neutral-600" />
        <CardTitle className="text-lg font-medium text-ss-neutral-950">
          Basic Information
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4">
        {/* Event Name & Category */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label
              variant="required"
              htmlFor="eventName"
              className="text-sm font-medium text-ss-neutral-950"
            >
              Event Name
            </Label>
            <Controller
              name="eventName"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Input
                    {...field}
                    id="eventName"
                    type="text"
                    placeholder="e.g., Summer Wellness Retreat"
                    className={cn(
                      'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20',
                      error && 'border-ss-critical-200'
                    )}
                    required
                    aria-invalid={!!error}
                  />
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="space-y-2">
            <Label
              variant="required"
              htmlFor="category"
              className="text-sm font-medium text-ss-neutral-950"
            >
              Category
            </Label>
            <Controller
              name="category"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger
                      size="lg"
                      className={cn(
                        'h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full',
                        error && 'border-ss-critical-200'
                      )}
                      aria-invalid={!!error}
                    >
                      <SelectValue placeholder="Select categories..." />
                    </SelectTrigger>
                    <SelectContent>
                      {transformCategoriesToOptions(categoryData)?.map(
                        category => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Controller
              name="tag"
              control={control}
              render={({ field }) => {
                const selectedIds = field.value?.map(t => t.id) || []
                const disableMore = selectedIds.length >= 10
                const disabledOptions = disableMore
                  ? multiSelectOptions
                      .filter(opt => !selectedIds.includes(opt.id))
                      .map(opt => opt.id)
                  : []

                return (
                  <>
                    <MultiSelect
                      label="Tags"
                      options={multiSelectOptions}
                      selected={selectedIds}
                      onChange={ids => handleTagChange(ids, field.onChange)}
                      placeholder="Select tags..."
                      required={false}
                      disabledOptions={disabledOptions}
                      className="h-11 border-ss-neutral-100 bg-ss-neutral-white hover:border-ss-blue-500 focus:border-ss-blue-500 w-full"
                    >
                      {selectedIds.length !== 0 && (
                        <div className="mt-1 text-sm text-muted-foreground">
                          <span className="text-sm">{selectedIds.length}</span>
                          <span className="text-sm">/10 tags</span>
                        </div>
                      )}
                    </MultiSelect>
                  </>
                )
              }}
            />
          </div>
        </div>

        {/* Event Description */}
        <div className="space-y-1">
          <Label
            variant="required"
            htmlFor="description"
            className="text-sm font-medium text-ss-neutral-950"
          >
            Event Description
          </Label>
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <div className="relative">
                <CustomRichTextEditor
                  value={field.value}
                  onChange={value =>
                    field.onChange(value === '<p></p>' ? '' : value)
                  }
                  placeholder="Add a clear and engaging description of your event."
                  className={error ? 'border-ss-critical-200' : ''}
                />
                {error && (
                  <p className="text-sm text-ss-critical-200 mt-1">
                    {error.message}
                  </p>
                )}
              </div>
            )}
          />
        </div>

        {/* Event Images */}
        <div className="space-y-1 max-w-[541px]">
          <Label
            variant="required"
            className="text-sm font-medium text-ss-neutral-950"
          >
            Event Images
          </Label>
          <Controller
            name="eventImages"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <FileUpload
                  multiple={true}
                  maxFiles={10}
                  existingFiles={field.value?.map(img => img.imageFile) || []}
                  onMultipleFileSelect={files =>
                    handleImageSelect(files, 'eventImage')
                  }
                  onRemoveFile={index => handleRemoveImage(index, 'eventImage')}
                  className={error ? 'border-ss-critical-200' : ''}
                />
                {error && (
                  <p className="text-sm text-ss-critical-200 mt-1">
                    {error.message}
                  </p>
                )}
              </>
            )}
          />
        </div>

        {/* Event PDF */}
        <div className="space-y-1 max-w-[541px]">
          <Label className="text-sm font-medium text-ss-neutral-950">
            BPF Schedule of Events PDF
          </Label>
          <Controller
            name="eventPDF"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const pdfFile = field.value?.pdfFile
              const hasPdf = pdfFile instanceof File

              return (
                <>
                  <FileUpload
                    key={
                      hasPdf
                        ? `pdf-${pdfFile.name}-${pdfFile.size}`
                        : 'pdf-empty'
                    }
                    isPdf={true}
                    multiple={false}
                    acceptedFileTypes={['application/pdf']}
                    maxFileSize={5 * 1024 * 1024}
                    onFileSelect={file => {
                      if (file) {
                        handleSelectPDF(file)
                      } else {
                        handleRemovePDF()
                      }
                    }}
                    previewImage={
                      hasPdf ? URL.createObjectURL(pdfFile) : undefined
                    }
                    previewImageHeadText={hasPdf ? pdfFile.name : ''}
                    className={error ? 'border-ss-critical-200' : ''}
                  />
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )
            }}
          />
        </div>

        {/* About Park City PDF */}
        <div className="space-y-1 max-w-[541px]">
          <Label className="text-sm font-medium text-ss-neutral-950">
            About Park City PDF
          </Label>
          <Controller
            name="eventInfoPDF"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const pdfFile = field.value?.pdfFile
              const hasPdf = pdfFile instanceof File

              return (
                <>
                  <FileUpload
                    key={
                      hasPdf
                        ? `info-pdf-${pdfFile.name}-${pdfFile.size}`
                        : 'info-pdf-empty'
                    }
                    isPdf={true}
                    multiple={false}
                    acceptedFileTypes={['application/pdf']}
                    maxFileSize={5 * 1024 * 1024}
                    onFileSelect={file => {
                      if (file) {
                        handleSelectInfoPDF(file)
                      } else {
                        handleRemoveInfoPDF()
                      }
                    }}
                    previewImage={
                      hasPdf ? URL.createObjectURL(pdfFile) : undefined
                    }
                    previewImageHeadText={hasPdf ? pdfFile.name : ''}
                    className={error ? 'border-ss-critical-200' : ''}
                  />
                  {error && (
                    <p className="text-sm text-ss-critical-200 mt-1">
                      {error.message}
                    </p>
                  )}
                </>
              )
            }}
          />
        </div>
      </CardContent>
    </>
  )
}
